import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  LegalHold,
  LegalHoldAvailableDocumentsResponse,
  LegalHoldCreate,
  LegalHoldDocument,
  LegalHoldDocumentCheckResponse,
  LegalHoldDocumentCreate,
  LegalHoldDocumentType,
  LegalHoldServerDocumentsResponse,
  LegalHoldStatus,
  LegalHoldWithDocuments,
} from '../../../../domain/src/lib/legal-hold/legal-hold.model';
import {
  resolveTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class LegalHoldService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly legalHoldPath = '/api/legal-hold';

  private legalHoldUrl(): string {
    return this.legalHoldPath;
  }

  list(status?: LegalHoldStatus): Observable<LegalHold[]> {
    const params = status
      ? new HttpParams().set('status_filter', status)
      : undefined;
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<LegalHold[]>(this.legalHoldUrl(), {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  get(id: string): Observable<LegalHoldWithDocuments> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.get<LegalHoldWithDocuments>(`${this.legalHoldUrl()}/${id}`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  create(data: LegalHoldCreate): Observable<LegalHold> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<LegalHold>(this.legalHoldUrl(), data, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  update(id: string, data: Partial<LegalHoldCreate>): Observable<LegalHold> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.put<LegalHold>(`${this.legalHoldUrl()}/${id}`, data, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  close(id: string): Observable<LegalHold> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<LegalHold>(`${this.legalHoldUrl()}/${id}/close`, {}, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  reopen(id: string): Observable<LegalHold> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<LegalHold>(`${this.legalHoldUrl()}/${id}/reopen`, {}, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  delete(id: string): Observable<{ message?: string }> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.delete<{ message?: string }>(`${this.legalHoldUrl()}/${id}`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  addDocument(id: string, data: LegalHoldDocumentCreate): Observable<LegalHoldDocument> {
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.post<LegalHoldDocument>(`${this.legalHoldUrl()}/${id}/documents`, data, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
    });
  }

  removeDocument(
    id: string,
    documentId: string,
    documentType: LegalHoldDocumentType,
  ): Observable<unknown> {
    const params = new HttpParams().set('document_type', documentType);
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.delete(`${this.legalHoldUrl()}/${id}/documents/${documentId}`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  checkDocument(
    documentId: string,
    documentType: LegalHoldDocumentType,
  ): Observable<LegalHoldDocumentCheckResponse> {
    const params = new HttpParams().set('document_type', documentType);
    const tenantId = resolveTenantId(this.serverContext);
    return this.http.get<LegalHoldDocumentCheckResponse>(
      `${this.legalHoldUrl()}/documents/check/${documentId}`,
      {
        headers: tenantHeaders(tenantId),
        params: withTenantParam(tenantId, params),
      },
    );
  }

  getAvailableDocuments(
    search?: string,
    documentType?: LegalHoldDocumentType,
    limit?: number,
    offset?: number,
  ): Observable<LegalHoldAvailableDocumentsResponse> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (documentType) params = params.set('document_type', documentType);
    if (typeof limit === 'number') params = params.set('limit', String(limit));
    if (typeof offset === 'number') params = params.set('offset', String(offset));
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<LegalHoldAvailableDocumentsResponse>(
      `${this.legalHoldUrl()}/documents/available`,
      {
        headers: tenantHeaders(tenantId),
        params: withTenantParam(tenantId, params),
      },
    );
  }

  getDocumentsFromServer(search?: string, limit?: number): Observable<LegalHoldServerDocumentsResponse> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    if (typeof limit === 'number') params = params.set('limit', String(limit));
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.get<LegalHoldServerDocumentsResponse>(
      `${this.legalHoldUrl()}/documents/from-server`,
      {
        headers: tenantHeaders(tenantId),
        params: withTenantParam(tenantId, params),
      },
    );
  }

  addDocumentByPath(id: string, filePath: string, reason?: string): Observable<LegalHoldDocument> {
    let params = new HttpParams().set('file_path', filePath);
    params = params.set('legal_hold_id', id);
    if (reason) params = params.set('reason', reason);
    const tenantId = resolveTenantId(this.serverContext);

    return this.http.post<LegalHoldDocument>(`${this.legalHoldUrl()}/documents/by-path`, null, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }
}
